import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, DrawerProps } from '@arco-design/web-react';
import { IconRight } from '@arco-design/web-react/icon';
import styles from './index.module.less';

// 修复类型定义：drawer 为必需属性
interface DrawerConfig {
  title: string;
  width?: string | number;
  onOpen?: () => void;
  onClose?: () => void;
  onCancel?: () => void;
  onConfirm?: (handleClose: () => void) => void;
  children?: React.ReactNode;
  footer?: React.ReactNode | boolean;
}

type IDrawerRowItemProps = {
  btnSrc?: string;
  btnText: string;
  suffix?: React.ReactNode;
  drawer: DrawerConfig & DrawerProps; // 修复：drawer 现在是必需的
} & React.HTMLAttributes<HTMLDivElement>;

function DrawerRowItem(props: IDrawerRowItemProps) {
  const { btnSrc, btnText, suffix, drawer, style, className = '' } = props;
  const [open, setOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // 使用 useCallback 优化事件处理函数
  const handleClose = useCallback(() => {
    if (isProcessing) return; // 防止重复操作

    setIsProcessing(true);
    try {
      drawer.onCancel?.();
      setOpen(false);
      drawer.onClose?.();
    } catch (error) {
      console.error('[DrawerRowItem] 关闭抽屉时出错:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [drawer, isProcessing]);

  const handleOpen = useCallback(() => {
    if (isProcessing) return; // 防止重复操作

    setIsProcessing(true);
    try {
      setOpen(true);
      drawer.onOpen?.();
    } catch (error) {
      console.error('[DrawerRowItem] 打开抽屉时出错:', error);
      setOpen(false); // 出错时重置状态
    } finally {
      setIsProcessing(false);
    }
  }, [drawer, isProcessing]);

  // 实现 onConfirm 回调处理
  const handleConfirm = useCallback(() => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      if (drawer.onConfirm) {
        drawer.onConfirm(handleClose);
      } else {
        // 如果没有 onConfirm，默认关闭抽屉
        handleClose();
      }
    } catch (error) {
      console.error('[DrawerRowItem] 确认操作时出错:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [drawer, handleClose, isProcessing]);

  // 组件卸载时的清理
  useEffect(() => {
    return () => {
      // 组件卸载时确保状态重置
      setIsProcessing(false);
    };
  }, []);

  return (
    <>
      <div
        style={style || {}}
        className={`${styles.row} ${className} ${isProcessing ? styles.processing : ''}`}
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOpen();
          }
        }}
      >
        <div className={styles.firstPart}>
          {btnSrc ? <img src={btnSrc} className={styles.icon} alt="svg" /> : ''}
          {btnText}
          {suffix}
        </div>
        <div className={styles.finalPart}>
          <IconRight className={styles.rightOutlined} />
        </div>
      </div>
      <Drawer
        closable
        width={drawer.width || 400}
        className={styles.drawer}
        visible={open}
        onCancel={handleClose}
        footer={
          drawer.footer !== false
            ? drawer.footer ||
              (drawer.onConfirm ? (
                <div className={styles.drawerFooter}>
                  <button
                    className={styles.cancelBtn}
                    onClick={handleClose}
                    disabled={isProcessing}
                  >
                    取消
                  </button>
                  <button
                    className={styles.confirmBtn}
                    onClick={handleConfirm}
                    disabled={isProcessing}
                  >
                    确认
                  </button>
                </div>
              ) : null)
            : null
        }
        {...drawer} // 传递其他 DrawerProps，包括 title
      >
        <div className={styles.children}>{drawer.children}</div>
      </Drawer>
    </>
  );
}

export default DrawerRowItem;
